<template>
  <div class="radio-card"> <!---v-if="hasProps" -->
    <input
      :id="id"
      v-model="selected"
      :name="name"
      :value="option.value"
      :disabled="disabled"
      type="radio"
      :class="{ invalid: hasErrors }"
      :data-test-base-radio-single-card-input="id"
      @change="$emit('update:modelValue', selected)"
    />
    <label
      :for="id"
      :data-test-base-radio-single-card-label="id"
      :class="[
        { 'no-description': !showDescription },
        { 'is-active': isActive },
        { 'label-with-slot': hasSlot }
      ]"
    >
      <strong
        v-if="showLabel"
        :data-test-base-radio-single-card-strong="id"
        v-html="option.label"
      />
      <div
        v-if="showDescription"
        :data-test-base-radio-single-card-description="id"
        v-html="option.description"
      />
    </label>
    <div v-if="hasSlot" class="info-link">
      <slot :for="name" name="infolink" />
    </div>
  </div>
</template>

<script lang="ts">

import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';
import { defineComponent, PropType } from 'vue';
import Option from '@/interfaces/IOption';

export default defineComponent({
  name: 'BaseRadioButtonSingleCard',
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  props: {
    id: {
      type: String,
      default() {
        return '';
      }
    },
    name: {
      type: String,
      default() {
        return 'default-radio-button-single-card-name';
      }
    },
    modelValue: {
      type: [Boolean, Number, String, Object] as PropType<boolean | number | string | object>,
      default() {
        return null;
      }
    },
    option: {
      type: Object as PropType<Option>,
      default() {
        return {
          label: '',
          description: ''
        };
      }
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      selected: this.modelValue
    };
  },
  computed: {
    hasProps(): boolean {
      return this.id !== '' &&
        this.name !== 'default-radio-button-single-card-name' &&
        this.modelValue !== null
        ? true
        : false;
    },
    hasErrors(): boolean {
      return this.name in this.form.errors._value;
      
    },
    hasSlot(): boolean {
      return !!this.$slots.infolink;
    },
    showLabel(): boolean {
      return this.option.label !== '' ? true : false;
    },
    showDescription(): boolean {
      return this.option.description !== '' ? true : false;
    },
    isActive(): boolean {
      return this.option.value === this.selected;
    }
  },
  watch: {
    modelValue(val: boolean | number | string | object) {
      this.selected = val;
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';
@import 'node_modules/foundation-sites/scss/util/util';
@import 'src/styles/typography/_fonts.scss';

.radio-card {
  position: relative;

  label {
    width: 100%;
    line-height: 1.4;
    border: 1px solid $medium-gray;
    border-radius: $button-radius;
    padding: rem-calc(20);
    margin-right: 0;

    strong {
      font-family: $meta-serif-bold;
      font-size: rem-calc(15);
      line-height: rem-calc(20);
      padding-left: rem-calc(45);
      margin-bottom: rem-calc(15);
      display: inline-block;
    }

    div {
      padding-left: rem-calc(45);
      margin-bottom: 0 !important;

      :last-child {
        margin-bottom: 0 !important;
      }
    }

    &:hover {
      border: 1px solid $primary-color;
      cursor: pointer;
    }

    &.is-active {
      border: 3px solid #a4a4a4;
      padding-top: rem-calc(18);
      padding-right: rem-calc(18);
      padding-left: rem-calc(18);
    }
  }

  :disabled {
    opacity: 0;

    & + label {
      opacity: 0.4;
      border: 1px solid $medium-gray;
      cursor: not-allowed;
    }
  }

  .invalid {
    & + label {
      border: 1px solid #{map-get($foundation-palette, 'alert')};
    }
  }

  .no-description {
    height: rem-calc(60);
  }

  .label-with-slot {
    padding-bottom: rem-calc(65);

    &.is-active {
      padding-bottom: rem-calc(63);
    }
  }

  .info-link {
    padding-left: rem-calc(65);
    position: absolute;
    bottom: rem-calc(35);
  }
}
</style>
